/*
  REST Text2Speech component, gets a sentance 
  and plays the answer on the browser 
 */

import { observer } from "mobx-react";
import React, {useRef, useState, useEffect} from "react";
import { store } from "stores/Store";
import { InterruptControllerRef } from "components/InterruptController/InterruptController";
  
  const getApiKey = async () => {
      const myDGKey = process.env.REACT_APP_DG_KEY || '';
      if (!myDGKey) {
          console.error('Deepgram API key not found');
      }
      return myDGKey;     // Got rid of hard-coding of the API Key.  TODO: need to protect this key by extracting from backend.   
  };

  interface IText2Speech {
      audioCtx: AudioContext;
      isVoicePlaying: boolean;
      setIsVoicePlaying: (isVoicePlaying: boolean) => void;
      interruptControllerRef: React.MutableRefObject<InterruptControllerRef | null>;
  }

  const DeepgramT2S: React.FC<IText2Speech> = observer(
    ({ audioCtx, isVoicePlaying, setIsVoicePlaying, interruptControllerRef }) => {
      // holds the current playing buffer source so we can stop it on interrupt
      const sourceRef = useRef<AudioBufferSourceNode | null>(null);
      const isProcessingRef = useRef<boolean>(false);

      // 1️⃣ When a new answer arrives, fetch, decode, and play via AudioContext
      useEffect(() => {
        const last = store.answers[store.answers.length - 1];
        if (!last?.message?.trim() || isProcessingRef.current) return;
      
          (async () => {
            try {
              isProcessingRef.current = true;
              const apiKey = await getApiKey();
              if (!apiKey) throw new Error("No API key");
  
              const response = await fetch(
                "https://api.deepgram.com/v1/speak?model=aura-athena-en",
                {
                  method: "POST",
                  headers: {
                    Authorization: `Token ${apiKey}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ text: last.message }),
                }
              );
              if (!response.ok) throw new Error(`TTS request failed ${response.status}`);
  
              // Check if we should stop before proceeding
              if (interruptControllerRef.current?.isForceStopped()) {
                throw new Error("Interrupted");
              }
  
              // get raw bytes
              const arrayBuffer = await response.arrayBuffer();
              // decode to AudioBuffer
              const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
  
              // Check again if we should stop
              if (interruptControllerRef.current?.isForceStopped()) {
                throw new Error("Interrupted");
              }
  
              // stop any prior playback
              if (sourceRef.current) {
                sourceRef.current.stop();
              }
  
              // create & play new buffer source
              const src = audioCtx.createBufferSource();
              src.buffer = audioBuffer;
              src.connect(audioCtx.destination);
              src.onended = () => {
                sourceRef.current = null;
                isProcessingRef.current = false;
                setIsVoicePlaying(false);
              };
              sourceRef.current = src;
              src.start(0);
              setIsVoicePlaying(true);
            } catch (err) {
              isProcessingRef.current = false;
              setIsVoicePlaying(false);
            }
          })();
      }, [store.answers.length, audioCtx, interruptControllerRef]);
  
      // 2️⃣ Handle interrupt signal
      useEffect(() => {
        if (!isVoicePlaying && sourceRef.current) {
          sourceRef.current.stop();
          sourceRef.current = null;
          isProcessingRef.current = false;
        }
      }, [isVoicePlaying]);
  
      // No <audio> tag needed—playback is via the AudioContext
      return null;
    }
  );
    
  export default DeepgramT2S;